import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  VStack,
  HStack,
  IconButton,
  Collapse,
  Button,
} from "@chakra-ui/react";

import {
  FiSearch,
  FiChevronDown,
  FiChevronRight,
  FiChevronsDown,
  FiChevronsRight,
  FiBookOpen,
  FiShield,
  FiUsers,
  FiFolder,
  FiCheckSquare,
  FiClock,
  FiTrendingUp,
  FiClipboard,
  FiMapPin,
  FiCalendar,
  FiMessageSquare,
  FiBarChart2,
  FiServer,
  FiSettings,
  FiHelpCircle,
  FiX,
  FiBook,
} from "react-icons/fi";
import { SectionNode } from "./types";
import { IconWrapper } from "../../components/IconWrapper";

const ChakraCollapse = Collapse as any;

interface HelpSidebarProps {
  sections: SectionNode[];
  activeSectionId: string;
  onSelectSection: (id: string, childId?: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isDark: boolean;
}

const getCategoryIcon = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes("introduction") || t.includes("guide")) return FiBook;
  if (t.includes("authentication") || t.includes("sign") || t.includes("login") || t.includes("forgot")) return FiShield;
  if (t.includes("user")) return FiUsers;
  if (t.includes("project")) return FiFolder;
  if (t.includes("task") || t.includes("gantt")) return FiCheckSquare;
  if (t.includes("timesheet")) return FiClock;
  if (t.includes("progress")) return FiTrendingUp;
  if (t.includes("kpi")) return FiClipboard;
  if (t.includes("seat") || t.includes("booking")) return FiMapPin;
  if (t.includes("calendar") || t.includes("leave")) return FiCalendar;
  if (t.includes("chat") || t.includes("team")) return FiMessageSquare;
  if (t.includes("sentiment") || t.includes("burn-out") || t.includes("analytics")) return FiBarChart2;
  if (t.includes("log") || t.includes("monitoring")) return FiServer;
  if (t.includes("settings") || t.includes("configuration") || t.includes("work group") || t.includes("department")) return FiSettings;
  return FiHelpCircle;
};

const HelpSidebar: React.FC<HelpSidebarProps> = ({
  sections,
  activeSectionId,
  onSelectSection,
  searchQuery,
  setSearchQuery,
  isDark,
}) => {
  const [expandedIds, setExpandedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("zs_help_expanded_categories");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const handleExpandAll = () => {
    const allIds = sections.map((s) => s.id);
    setExpandedIds(allIds);
  };

  const handleCollapseAll = () => {
    setExpandedIds([]);
  };

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input on "/" keypress
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Save expanded states
  useEffect(() => {
    localStorage.setItem("zs_help_expanded_categories", JSON.stringify(expandedIds));
  }, [expandedIds]);

  // Automatically expand parents if a search query is active
  useEffect(() => {
    if (searchQuery.trim() !== "") {
      const parentIdsToExpand: string[] = [];
      sections.forEach((section) => {
        const matchesChild = section.children.some(
          (child) =>
            child.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            child.content.toLowerCase().includes(searchQuery.toLowerCase())
        );
        const matchesParent =
          section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          section.content.toLowerCase().includes(searchQuery.toLowerCase());

        if (matchesChild || matchesParent) {
          parentIdsToExpand.push(section.id);
        }
      });
      setExpandedIds((prev) => {
        const next = [...prev];
        parentIdsToExpand.forEach((id) => {
          if (!next.includes(id)) next.push(id);
        });
        return next;
      });
    }
  }, [searchQuery, sections]);

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isExpanded = (id: string) => expandedIds.includes(id);

  // Helper to highlight search term in title
  const highlightTitle = (title: string, query: string) => {
    if (!query) return <span>{title}</span>;
    const escapeRegExp = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };
    const parts = title.split(new RegExp(`(${escapeRegExp(query)})`, "gi"));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark
              key={i}
              style={{
                backgroundColor: "rgba(181, 145, 67, 0.25)",
                color: "#FBD282",
                paddingLeft: "2px",
                paddingRight: "2px",
                borderRadius: "2px",
                fontWeight: "semibold",
              }}
            >
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  // Check if a section or its children match search query
  const matchesSearch = (section: SectionNode): boolean => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    
    // Check parent title or content
    if (section.title.toLowerCase().includes(q) || section.content.toLowerCase().includes(q)) {
      return true;
    }
    
    // Check child titles or content
    return section.children.some(
      (child) => child.title.toLowerCase().includes(q) || child.content.toLowerCase().includes(q)
    );
  };

  // Find if a section is active or contains the active child
  const isSectionActive = (section: SectionNode) => {
    if (activeSectionId === section.id) return true;
    return section.children.some((child) => activeSectionId === child.id);
  };

  return (
    <Box
      w="280px"
      minW="280px"
      h="100%"
      display="flex"
      flexDirection="column"
      borderRight="1px solid"
      borderColor={isDark ? "#2E2F33" : "#E5E7EB"}
      bg={isDark ? "#0B0B0F" : "#FAFBFC"}
      className="select-none"
    >
      {/* Search Input Box */}
      <Box p={4} borderBottom="1px solid" borderColor={isDark ? "#1E2024" : "#EDF2F7"}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <IconWrapper icon={FiSearch} color={isDark ? "#B59143" : "#718096"} size={16} />
          </InputLeftElement>
          <Input
            ref={searchInputRef}
            placeholder="Search docs... (Press '/')"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg={isDark ? "#14161B" : "#FFFFFF"}
            border="1px solid"
            borderColor={isDark ? "#2E2F33" : "#E2E8F0"}
            borderRadius="lg"
            fontSize="xs"
            color={isDark ? "#FFFFFF" : "#1A202C"}
            _placeholder={{ color: isDark ? "#52525B" : "#A0AEC0" }}
            _focus={{
              borderColor: "#B59143",
              boxShadow: "0 0 0 1px #B59143",
            }}
            _hover={{
              borderColor: isDark ? "#3F3F46" : "#CBD5E0",
            }}
          />
          <InputRightElement width="2.5rem">
            {searchQuery ? (
              <IconButton
                aria-label="Clear search"
                icon={<IconWrapper icon={FiX} size={12} />}
                size="xs"
                variant="ghost"
                color={isDark ? "#A0AEC0" : "#718096"}
                _hover={{ bg: "transparent", color: "#B59143" }}
                onClick={() => setSearchQuery("")}
              />
            ) : (
              <Box
                px={1.5}
                py={0.5}
                bg={isDark ? "#1E2024" : "#EDF2F7"}
                border="1px solid"
                borderColor={isDark ? "#2E2F33" : "#E2E8F0"}
                borderRadius="md"
                fontSize="9px"
                fontWeight="bold"
                color={isDark ? "#718096" : "#A0AEC0"}
              >
                /
              </Box>
            )}
          </InputRightElement>
        </InputGroup>
      </Box>

      {/* Utility Bar: Expand / Collapse All */}
      <HStack
        px={4}
        py={2}
        justify="space-between"
        borderBottom="1px solid"
        borderColor={isDark ? "#1E2024" : "#EDF2F7"}
        bg={isDark ? "#09090D" : "#F9FAFB"}
      >
        <Text
          fontSize="10px"
          fontWeight="700"
          color={isDark ? "#718096" : "#718096"}
          letterSpacing="wider"
          textTransform="uppercase"
        >
          Categories
        </Text>
        <Button
          size="xs"
          variant="link"
          fontSize="10px"
          fontWeight="600"
          color="#B59143"
          _hover={{ textDecoration: "none", color: isDark ? "#FBD282" : "#947632" }}
          onClick={() => {
            if (expandedIds.length > 0) {
              handleCollapseAll();
            } else {
              handleExpandAll();
            }
          }}
        >
          {expandedIds.length > 0 ? "Collapse All" : "Expand All"}
        </Button>
      </HStack>

      {/* Categories Tree Navigation */}
      <Box
        flex={1}
        overflowY="auto"
        px={3}
        py={4}
        sx={{
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" }
        }}
      >
        <VStack align="stretch" spacing={1}>
          {sections.filter(matchesSearch).map((section) => {
            const hasChildren = section.children.length > 0;
            const expanded = isExpanded(section.id);
            const parentActive = isSectionActive(section);
            const sectionHighlighted = activeSectionId === section.id;
            const CategoryIcon = getCategoryIcon(section.title);

            return (
              <Box key={section.id} role="group">
                <HStack
                  px={3}
                  py={2.5}
                  spacing={3}
                  alignItems="center"
                  cursor="pointer"
                  borderRadius="md"
                  transition="all 0.2s"
                  bg={
                    parentActive
                      ? isDark
                        ? "rgba(181, 145, 67, 0.06)"
                        : "rgba(181, 145, 67, 0.03)"
                      : "transparent"
                  }
                  borderLeft="3px solid"
                  borderColor={parentActive ? "#B59143" : "transparent"}
                  color={
                    parentActive
                      ? isDark
                        ? "#FBD282"
                        : "#B59143"
                      : isDark
                      ? "#C3C9D3"
                      : "#4A5568"
                  }
                  _hover={{
                    bg: parentActive
                      ? undefined
                      : isDark
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.03)",
                    color: isDark ? "#FFFFFF" : "#111827",
                  }}
                  onClick={() => {
                    onSelectSection(section.id);
                    if (hasChildren) {
                      setExpandedIds((prev) =>
                        prev.includes(section.id)
                          ? prev.filter((item) => item !== section.id)
                          : [...prev, section.id]
                      );
                    }
                  }}
                >
                  {/* Category Dynamic Icon */}
                  <Box
                    className="transition-colors duration-200"
                    color={
                      sectionHighlighted
                        ? "#B59143"
                        : isDark
                        ? "#718096"
                        : "#A0AEC0"
                    }
                    _groupHover={{ color: "#B59143" }}
                  >
                    <IconWrapper icon={CategoryIcon} size={15} />
                  </Box>

                  {/* Title Text */}
                  <Text
                    fontSize="13px"
                    fontWeight={parentActive ? "600" : "500"}
                    textAlign="left"
                    noOfLines={1}
                    flex={1}
                  >
                    {highlightTitle(section.title, searchQuery)}
                  </Text>

                  {/* Children Document Count Badge */}
                  {hasChildren && (
                    <Box
                      px={1.5}
                      py={0.2}
                      borderRadius="full"
                      bg={
                        parentActive
                          ? isDark
                            ? "rgba(181, 145, 67, 0.2)"
                            : "rgba(181, 145, 67, 0.1)"
                          : isDark
                          ? "#1E2024"
                          : "#EDF2F7"
                      }
                      fontSize="9px"
                      color={
                        parentActive
                          ? "#FBD282"
                          : isDark
                          ? "#718096"
                          : "#718096"
                      }
                    >
                      {section.children.length}
                    </Box>
                  )}

                  {/* Expand/Collapse Chevron */}
                  {hasChildren && (
                    <Box
                      onClick={(e) => toggleExpand(section.id, e)}
                      cursor="pointer"
                      borderRadius="md"
                      color={isDark ? "#718096" : "#A0AEC0"}
                      _hover={{
                        bg: isDark ? "#1E2024" : "#EDF2F7",
                        color: "#B59143",
                      }}
                      className="transition-transform duration-200"
                    >
                      <IconWrapper icon={expanded ? FiChevronDown : FiChevronRight} size={14} />
                    </Box>
                  )}
                </HStack>

                {/* Subsections Collapse */}
                {hasChildren && (
                  <ChakraCollapse in={expanded} animateOpacity>
                    <VStack align="stretch" spacing={0.5} mt={1} pl={7} borderLeft="1px solid" borderColor={isDark ? "#1E2024" : "#EDF2F7"} ml={5.5}>
                      {section.children
                        .filter(
                          (child) =>
                            !searchQuery ||
                            child.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            child.content.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map((child) => {
                          const childActive = activeSectionId === child.id;

                          return (
                            <HStack
                              key={child.id}
                              px={3}
                              py={1.5}
                              spacing={2}
                              alignItems="center"
                              cursor="pointer"
                              borderRadius="md"
                              transition="all 0.2s"
                              bg={
                                childActive
                                  ? isDark
                                    ? "rgba(181, 145, 67, 0.08)"
                                    : "rgba(181, 145, 67, 0.05)"
                                  : "transparent"
                              }
                              color={
                                childActive
                                  ? isDark
                                    ? "#FBD282"
                                    : "#B59143"
                                  : isDark
                                  ? "#A0AEC0"
                                  : "#6B7280"
                              }
                              _hover={{
                                bg: childActive
                                  ? undefined
                                  : isDark
                                  ? "rgba(255,255,255,0.02)"
                                  : "rgba(0,0,0,0.02)",
                                color: isDark ? "#FFFFFF" : "#1A202C",
                              }}
                              onClick={() => onSelectSection(section.id, child.id)}
                            >
                              {/* Left bullet marker */}
                              <Box
                                w="4px"
                                h="4px"
                                borderRadius="full"
                                bg={childActive ? "#B59143" : isDark ? "#3F3F46" : "#E2E8F0"}
                              />
                              
                              <Text
                                fontSize="12px"
                                fontWeight={childActive ? "500" : "400"}
                                textAlign="left"
                                noOfLines={1}
                              >
                                {highlightTitle(child.title, searchQuery)}
                              </Text>
                            </HStack>
                          );
                        })}
                    </VStack>
                  </ChakraCollapse>
                )}
              </Box>
            );
          })}

          {/* Empty Search Result */}
          {sections.filter(matchesSearch).length === 0 && (
            <Box py={8} textAlign="center">
              <Text fontSize="xs" color={isDark ? "#718096" : "#A0AEC0"}>
                No categories match your search.
              </Text>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default HelpSidebar;
